import { useEffect } from 'react';
import { useCompassCard } from '@scale/compass-ui/store';
import {
  type ContributorsAdapterContext,
  type ContributorSearchMode,
} from '@scale/genai-compass-shared/cards/contributors_adapter';
import { type FilterGroup, isValidFilterGroup } from '@scale/compass-shared/filter';

function useContributorsAdapterCard(index: number) {
  const { card, updateCardContext, updateCardCanSubmit } = useCompassCard(index);
  const context = card.defaultContext as ContributorsAdapterContext;

  const setSearchMode = (searchMode: ContributorSearchMode) => {
    const updatedContext: ContributorsAdapterContext = {
      ...context,
      searchMode,
    };
    updateCardContext(updatedContext);
  };

  const setContributorIdsOrEmails = (contributorIdsOrEmails: string[]) => {
    const updatedContext: ContributorsAdapterContext = {
      ...context,
      contributorIdsOrEmails,
    };
    updateCardContext(updatedContext);
  };

  const setFilter = (filter: FilterGroup) => {
    const updatedContext: ContributorsAdapterContext = {
      ...context,
      filter,
    };
    updateCardContext(updatedContext);
  };

  const setIncludeAllProjectData = (includeAllProjectData: boolean) => {
    const updatedContext: ContributorsAdapterContext = {
      ...context,
      includeAllProjectData,
    };
    updateCardContext(updatedContext);
  };

  const setIncludeAllWorkerSkillData = (includeAllWorkerSkillData: boolean) => {
    const updatedContext: ContributorsAdapterContext = {
      ...context,
      includeAllWorkerSkillData,
    };
    updateCardContext(updatedContext);
  };

  const setExcludeFraudHolding = (excludeFraudHolding: boolean) => {
    const updatedContext: ContributorsAdapterContext = {
      ...context,
      excludeFraudHolding,
    };
    updateCardContext(updatedContext);
  };

  // Update canSubmit based on filter or search.
  useEffect(() => {
    let canSubmit = true;

    if (context.searchMode === 'Search') {
      if (!context.contributorIdsOrEmails.length) {
        canSubmit = false;
      }
    } else if (context.searchMode === 'Filter') {
      canSubmit = isValidFilterGroup(context.filter);
    } else {
      canSubmit = false;
    }

    updateCardCanSubmit(canSubmit);
  }, [context.searchMode, context.filter, context.contributorIdsOrEmails]);

  return {
    filter: context.filter,
    setFilter,
    setContributorIdsOrEmails,
    searchMode: context.searchMode,
    setSearchMode,
    contributorIdsOrEmails: context.contributorIdsOrEmails,
    includeAllProjectData: context.includeAllProjectData,
    setIncludeAllProjectData,
    includeAllWorkerSkillData: context.includeAllWorkerSkillData,
    setIncludeAllWorkerSkillData,
    excludeFraudHolding: context.excludeFraudHolding,
    setExcludeFraudHolding,
  };
}

export default useContributorsAdapterCard;
